/**
 * =============================================================================
 * IDS CRM Web - the phone's copy of unsent Log Work changes (Phase 6 step 7)
 * =============================================================================
 *
 * Spec 6.1. Every change to a saved Log Work is written here FIRST, then sent.
 * It is removed once the server has it. So a change made with no signal, or
 * one whose reply was lost, is still on the phone after the page is closed or
 * the phone restarts, and is sent when the Log Work is next opened.
 *
 * IndexedDB 'ids-crm-log-work', version 1:
 *  - store 'parts', keyPath ['userId', 'taskId', 'draftKey', 'partKey'],
 *    where draftKey is the draft id or 'none' (a change made before the phone
 *    knew which saved Log Work it belongs to). Value: { userId, taskId,
 *    draftId, draftKey, partKey, value, baseVersion, clientChangeId,
 *    sentValue, changedAt, openedAt, state: 'pending'|'failed'|'stale',
 *    failMessage }.
 *  - store 'photos', keyPath 'clientRef', index 'byUserTask' on
 *    ['userId', 'taskId']. Value: { clientRef, userId, taskId, draftId,
 *    partKey, stepKey, name, type, size, blob, addedAt, state, failMessage }.
 *
 * Kept per user id. Never cleared on a 401 or on sign-out: a change is only
 * thrown away when the Log Work it belongs to has been saved (clearTask) or
 * the engineer chooses Discard.
 *
 * Safety: every call is wrapped, and none ever throws. Where IndexedDB is
 * missing (an old browser, a private window, jsdom in tests) or will not
 * open, the store keeps changes in memory instead and says so (persistent:
 * false, NO_PHONE_STORE_MESSAGE): the changes are still sent, but are lost if
 * the page closes before they are. A full phone (QuotaExceededError) is
 * reported as { ok: false, error: 'quota', message: PHONE_FULL_MESSAGE }.
 *
 * The IndexedDB factory is injectable (createLogWorkPhoneStore({ indexedDB })),
 * so tests use a small in-memory fake with no new dependency.
 * =============================================================================
 */

export const DB_NAME = 'ids-crm-log-work';
export const DB_VERSION = 1;
export const PARTS_STORE = 'parts';
export const PHOTOS_STORE = 'photos';
export const NO_DRAFT_KEY = 'none';

export const NO_PHONE_STORE_MESSAGE = "This browser cannot keep unsent changes. Stay in signal until 'Saved' shows.";
export const PHONE_FULL_MESSAGE = 'This phone is out of space for photos. Let the waiting photos send before adding more.';

const UNSENT_STATES = ['pending', 'failed', 'stale'];

export const draftKeyOf = (draftId) => (draftId ? String(draftId) : NO_DRAFT_KEY);

const isQuotaError = (error) => Boolean(error) && (
  error.name === 'QuotaExceededError'
  || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
  || error.code === 22
);

const partId = (entry) => [String(entry.userId), String(entry.taskId), String(entry.draftKey), String(entry.partKey)];
const partIdText = (entry) => JSON.stringify(partId(entry));

// ---------------------------------------------------------------------------
// Back ends: memory and IndexedDB, both with the same small promise API.
// ---------------------------------------------------------------------------

/** Changes kept in memory only: used where IndexedDB cannot be. */
export function createMemoryBackend() {
  const parts = new Map();
  const photos = new Map();
  const copy = (value) => (value ? { ...value } : value);
  return {
    persistent: false,
    async putPart(entry) { parts.set(partIdText(entry), copy(entry)); },
    async getAllParts() { return [...parts.values()].map(copy); },
    async deletePart(entry) { parts.delete(partIdText(entry)); },
    async putPhoto(entry) { photos.set(String(entry.clientRef), copy(entry)); },
    async getAllPhotos() { return [...photos.values()].map(copy); },
    async deletePhoto(clientRef) { photos.delete(String(clientRef)); }
  };
}

/** Runs one request inside its own transaction and waits for the transaction to finish. */
function inTransaction(db, storeName, mode, makeRequest) {
  return new Promise((resolve, reject) => {
    let result;
    let settled = false;
    const fail = (error) => {
      if (settled) return;
      settled = true;
      reject(error || new Error('IndexedDB transaction failed'));
    };
    try {
      const tx = db.transaction([storeName], mode);
      tx.oncomplete = () => {
        if (settled) return;
        settled = true;
        resolve(result);
      };
      tx.onerror = () => fail(tx.error);
      tx.onabort = () => fail(tx.error);
      const request = makeRequest(tx.objectStore(storeName));
      request.onsuccess = () => { result = request.result; };
      request.onerror = () => fail(request.error);
    } catch (error) {
      fail(error);
    }
  });
}

/**
 * Opens the database, or rejects. Opening can hang when another tab holds an
 * older version open ('blocked'), so it gives up after a few seconds.
 */
function openDatabase(factory, { timeoutMs = 4000 } = {}) {
  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      fn(value);
    };
    const timer = setTimeout(() => finish(reject, new Error('IndexedDB did not open in time')), timeoutMs);
    try {
      const request = factory.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(PARTS_STORE)) {
          const parts = db.createObjectStore(PARTS_STORE, { keyPath: ['userId', 'taskId', 'draftKey', 'partKey'] });
          parts.createIndex('byUserTask', ['userId', 'taskId'], { unique: false });
        }
        if (!db.objectStoreNames.contains(PHOTOS_STORE)) {
          const photos = db.createObjectStore(PHOTOS_STORE, { keyPath: 'clientRef' });
          photos.createIndex('byUserTask', ['userId', 'taskId'], { unique: false });
        }
      };
      request.onsuccess = () => finish(resolve, request.result);
      request.onerror = () => finish(reject, request.error || new Error('IndexedDB would not open'));
      request.onblocked = () => finish(reject, new Error('IndexedDB is blocked by another tab'));
    } catch (error) {
      finish(reject, error);
    }
  });
}

function createIndexedDbBackend(db) {
  return {
    persistent: true,
    putPart: (entry) => inTransaction(db, PARTS_STORE, 'readwrite', store => store.put(entry)),
    getAllParts: () => inTransaction(db, PARTS_STORE, 'readonly', store => store.getAll()).then(rows => rows || []),
    deletePart: (entry) => inTransaction(db, PARTS_STORE, 'readwrite', store => store.delete(partId(entry))),
    putPhoto: (entry) => inTransaction(db, PHOTOS_STORE, 'readwrite', store => store.put(entry)),
    getAllPhotos: () => inTransaction(db, PHOTOS_STORE, 'readonly', store => store.getAll()).then(rows => rows || []),
    deletePhoto: (clientRef) => inTransaction(db, PHOTOS_STORE, 'readwrite', store => store.delete(String(clientRef)))
  };
}

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

function defaultFactory() {
  try {
    if (typeof window !== 'undefined' && window.indexedDB) return window.indexedDB;
  } catch (error) {
    // Some browsers throw on simply reading indexedDB with storage blocked.
  }
  return null;
}

/**
 * @param {object}   [options]
 * @param {object}   [options.indexedDB]  An IDBFactory; defaults to window.indexedDB. null forces memory.
 * @param {number}   [options.openTimeoutMs]
 * @param {Function} [options.onError]    Told (error) about any failure, for logging only.
 */
export function createLogWorkPhoneStore(options = {}) {
  const factory = Object.prototype.hasOwnProperty.call(options, 'indexedDB') ? options.indexedDB : defaultFactory();
  const report = typeof options.onError === 'function' ? options.onError : () => {};
  const memory = createMemoryBackend();
  let backend = null;
  let opening = null;

  const store = {
    persistent: false,
    message: null
  };

  const switchToMemory = (error) => {
    if (error) report(error);
    backend = memory;
    store.persistent = false;
    store.message = NO_PHONE_STORE_MESSAGE;
    return backend;
  };

  function ready() {
    if (backend) return Promise.resolve(backend);
    if (!opening) {
      opening = (async () => {
        if (!factory || typeof factory.open !== 'function') return switchToMemory(null);
        try {
          const db = await openDatabase(factory, { timeoutMs: options.openTimeoutMs });
          backend = createIndexedDbBackend(db);
          store.persistent = true;
          store.message = null;
          return backend;
        } catch (error) {
          return switchToMemory(error);
        }
      })();
    }
    return opening;
  }

  /**
   * Runs one write. A full phone is reported; any other failure moves the
   * store to memory for the rest of this session and writes there, so the
   * change is still sent.
   */
  async function write(run) {
    let current;
    try {
      current = await ready();
      await run(current);
      return { ok: true, persistent: current.persistent };
    } catch (error) {
      if (isQuotaError(error)) {
        report(error);
        return { ok: false, error: 'quota', message: PHONE_FULL_MESSAGE };
      }
      if (current && current !== memory) {
        try {
          await run(switchToMemory(error));
          return { ok: true, persistent: false };
        } catch (memoryError) {
          report(memoryError);
        }
      } else {
        report(error);
      }
      return { ok: false, error: 'failed', message: NO_PHONE_STORE_MESSAGE };
    }
  }

  async function read(run, fallback) {
    try {
      const current = await ready();
      return await run(current);
    } catch (error) {
      report(error);
      try {
        return await run(memory);
      } catch (memoryError) {
        return fallback;
      }
    }
  }

  const sameTask = (row, userId, taskId) => String(row.userId) === String(userId) && String(row.taskId) === String(taskId);

  /** Whether IndexedDB is in use (after the first call; false until then). */
  store.ready = () => ready().then(current => current.persistent).catch(() => false);

  /** Saves (or replaces) the phone's copy of one part. */
  store.putPart = (entry) => {
    if (!entry || !entry.userId || !entry.taskId || !entry.partKey) {
      return Promise.resolve({ ok: false, error: 'failed', message: 'That change is missing its job or part.' });
    }
    const row = {
      state: 'pending',
      failMessage: null,
      ...entry,
      draftId: entry.draftId || null,
      draftKey: draftKeyOf(entry.draftId)
    };
    return write(current => current.putPart(row));
  };

  /** Every part copy for one user and job, whatever its draft. */
  store.getParts = (userId, taskId) => read(async current => (await current.getAllParts())
    .filter(row => sameTask(row, userId, taskId)), []);

  /** Removes one part copy. */
  store.deletePart = (userId, taskId, draftId, partKey) => write(current => current.deletePart({
    userId, taskId, draftKey: draftKeyOf(draftId), partKey
  }));

  /** Saves (or replaces) one photo waiting to be sent, keyed by clientRef. */
  store.putPhoto = (entry) => {
    if (!entry || !entry.clientRef || !entry.userId || !entry.taskId) {
      return Promise.resolve({ ok: false, error: 'failed', message: 'That photo is missing its job.' });
    }
    return write(current => current.putPhoto({ state: 'pending', failMessage: null, ...entry, draftId: entry.draftId || null }));
  };

  /** Every photo waiting on the phone for one user and job. */
  store.listPhotos = (userId, taskId) => read(async current => (await current.getAllPhotos())
    .filter(row => sameTask(row, userId, taskId)), []);

  store.deletePhoto = (clientRef) => write(current => current.deletePhoto(clientRef));

  /**
   * Jobs with changes on this phone not yet sent, for Today's "Log Work to
   * finish" card: [{ taskId, draftIds, pending, failed, stale, staleDraft,
   * lastChangedAt }], newest first. staleDraft is true when a copy is known
   * to belong to a Log Work that was saved or cleared since.
   *
   * @param {string} userId
   * @param {object} [options]
   * @param {Object<string,string|null>} [options.activeDrafts]  { taskId: active draft id or null }, when known.
   */
  store.listTasksWithUnsent = (userId, { activeDrafts } = {}) => read(async current => {
    const byTask = new Map();
    const touch = (row, isPhoto) => {
      if (String(row.userId) !== String(userId) || !UNSENT_STATES.includes(row.state || 'pending')) return;
      const taskId = String(row.taskId);
      if (!byTask.has(taskId)) {
        byTask.set(taskId, { taskId, draftIds: [], pending: 0, failed: 0, stale: 0, staleDraft: false, lastChangedAt: null });
      }
      const item = byTask.get(taskId);
      const state = row.state || 'pending';
      item[state] += 1;
      const draftId = row.draftId || null;
      if (draftId && !item.draftIds.includes(draftId)) item.draftIds.push(draftId);
      if (state === 'stale') item.staleDraft = true;
      if (activeDrafts && Object.prototype.hasOwnProperty.call(activeDrafts, taskId) && draftId
        && activeDrafts[taskId] !== draftId) {
        item.staleDraft = true;
      }
      const when = isPhoto ? row.addedAt : row.changedAt;
      if (when && (!item.lastChangedAt || String(when) > String(item.lastChangedAt))) item.lastChangedAt = when;
    };
    (await current.getAllParts()).forEach(row => touch(row, false));
    (await current.getAllPhotos()).forEach(row => touch(row, true));
    return [...byTask.values()].sort((a, b) => String(b.lastChangedAt || '').localeCompare(String(a.lastChangedAt || '')));
  }, []);

  /**
   * Removes every part copy and photo for one user, job and draft (after that
   * draft's Complete Work Log, or a Discard). draftId null clears the 'none' copies.
   *
   * keepStale leaves the stale copies alone: changes the server never took
   * (made after someone else saved or cleared that Log Work). Saving another
   * Log Work did not file them, so only the engineer's Copy or Discard may
   * remove them; a Parts used line among them would otherwise be lost.
   *
   * @param {object} [options]
   * @param {boolean} [options.keepStale]
   */
  store.clearTask = async (userId, taskId, draftId, { keepStale = false } = {}) => {
    const key = draftKeyOf(draftId);
    const parts = await store.getParts(userId, taskId);
    const photos = await store.listPhotos(userId, taskId);
    const kept = (row) => keepStale && row.state === 'stale';
    let ok = true;
    for (const row of parts) {
      if (draftKeyOf(row.draftId) !== key || kept(row)) continue;
      const result = await store.deletePart(userId, taskId, row.draftId, row.partKey);
      ok = ok && result.ok;
    }
    for (const row of photos) {
      if (draftKeyOf(row.draftId) !== key || kept(row)) continue;
      const result = await store.deletePhoto(row.clientRef);
      ok = ok && result.ok;
    }
    return { ok };
  };

  return store;
}

let sharedStore = null;

/** The one store this page uses (window.indexedDB, or memory). */
export function getLogWorkPhoneStore() {
  if (!sharedStore) sharedStore = createLogWorkPhoneStore();
  return sharedStore;
}

/** Tests only: forget the shared store. */
export function resetLogWorkPhoneStoreForTests() {
  sharedStore = null;
}

export default getLogWorkPhoneStore;
